// Browser speech: mic dictation into the composer and read-aloud of replies, both through the
// Web Speech API. This is the foundation layer only: the real voice pipeline (LiveKit, Pipecat
// worker, nemotron ASR and Kokoro on the Sparks) arrives in V1.5 per docs/runbooks/voice-v1.5.md.
// Nothing here talks to the server, and everything fails quietly when the browser lacks support.

type RecognitionAlternative = { transcript: string };
type RecognitionResult = { isFinal: boolean; 0: RecognitionAlternative };
type RecognitionEvent = { resultIndex: number; results: { length: number; [index: number]: RecognitionResult } };
type RecognitionErrorEvent = { error: string };
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onnomatch: (() => void) | null;
  start(): void;
  stop(): void;
};
type SpeechRecognitionCtor = new () => Recognition;

interface WindowWithSpeechRecognition {
  SpeechRecognition?: SpeechRecognitionCtor;
}

export function dictationSupported(): boolean {
  return typeof window !== 'undefined' && !!(window as unknown as WindowWithSpeechRecognition).SpeechRecognition;
}

/** The mic exists and the server dictation path is worth trying (it falls back to the browser recognizer itself). */
export function recordingSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/** True once one segment has been transcribed by the server (or the fallback engaged). */
let serverDictationBroken = false;

/**
 * Mic dictation through the server worker: audio is captured in self-contained 3 s segments
 * (stop/start, not timeslice: MediaRecorder chunks after the first are not independently decodable)
 * and each segment is POSTed to /api/asr. Finals land through onFinal exactly like the browser
 * recognizer. If the server path fails, the browser recognizer takes over transparently.
 */
export function startSmartDictation(handlers: DictationHandlers): () => void {
  if (!recordingSupported() || serverDictationBroken) return startDictation(handlers);

  let stopped = false;
  let recorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let browserFallback: (() => void) | null = null;

  const fail = (message: string): void => {
    if (stopped) return;
    serverDictationBroken = true;
    cleanup();
    if (dictationSupported()) {
      browserFallback = startDictation(handlers);
    } else {
      handlers.onError?.(message);
    }
  };

  const cleanup = (): void => {
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    recorder = null;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
  };

  const send = async (blob: Blob): Promise<void> => {
    try {
      const response = await fetch('/api/asr', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': blob.type || 'audio/webm' },
        body: blob,
      });
      if (!response.ok) {
        fail('Voice service unavailable');
        return;
      }
      const body = (await response.json()) as { text?: string };
      const text = body.text?.trim();
      if (text) handlers.onFinal(text);
    } catch {
      fail('Voice service unavailable');
    }
  };

  const start = async (): Promise<void> => {
    if (stopped) return;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      handlers.onError?.('Microphone permission refused');
      return;
    }
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : '';
    const record = (): void => {
      if (stopped || !stream) return;
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        if (chunks.length > 0) void send(new Blob(chunks, { type: recorder?.mimeType || 'audio/webm' }));
        if (!stopped) record();
      };
      recorder.start();
      window.setTimeout(() => {
        if (recorder && recorder.state === 'recording') recorder.stop();
      }, 3000);
    };
    record();
  };

  void start();

  return () => {
    stopped = true;
    cleanup();
    browserFallback?.();
  };
}

export function speakingSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export interface DictationHandlers {
  /** A finished phrase; called with just the new words. */
  onFinal: (text: string) => void;
  /** The phrase still being spoken; replaced on every update. */
  onInterim?: (text: string) => void;
  onEnd?: (reason: 'stopped' | 'error' | 'nomatch') => void;
  /** Human-readable reason the mic did not work (permission refused, no microphone). */
  onError?: (message: string) => void;
}

/**
 * Starts continuous dictation. Returns a stop function; calling it is always safe, even after
 * the engine stopped on its own. Browsers end the session on silence; this restarts it, so one
 * tap runs for a whole message.
 */
export function startDictation(handlers: DictationHandlers): () => void {
  let stopped = false;
  let recognition: Recognition | null = null;

  const start = (): void => {
    if (stopped) return;
    try {
      const Ctor = (window as unknown as WindowWithSpeechRecognition).SpeechRecognition;
      if (!Ctor) return;
      const rec = new Ctor();
      recognition = rec;
      rec.lang = navigator.language || 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (event: RecognitionEvent) => {
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          if (!result) continue;
          const text = result[0]?.transcript ?? '';
          if (result.isFinal) {
            if (text.trim()) handlers.onFinal(text.trim());
            handlers.onInterim?.('');
          } else {
            handlers.onInterim?.(text);
          }
        }
      };
      rec.onerror = (event: RecognitionErrorEvent) => {
        if (stopped) return;
        if (event.error === 'no-speech' || event.error === 'aborted') return; // the restart in onend covers these
        handlers.onError?.(
          event.error === 'not-allowed' || event.error === 'service-not-allowed'
            ? 'Microphone permission was refused. Allow the mic in the browser and try again.'
            : `Dictation error: ${event.error}.`,
        );
      };
      rec.onnomatch = () => handlers.onEnd?.('nomatch');
      rec.onend = () => {
        if (stopped) {
          handlers.onEnd?.('stopped');
          return;
        }
        // Silence or a network hiccup ends the session; keep going until the person stops us.
        setTimeout(() => {
          if (!stopped) start();
        }, 250);
      };
      rec.start();
    } catch {
      handlers.onError?.('Dictation is not available right now.');
    }
  };

  start();
  return () => {
    stopped = true;
    try {
      recognition?.stop();
    } catch {
      // Already stopped.
    }
  };
}

/** Reduces markdown to something that sounds right when spoken. */
export function stripMarkdownForSpeech(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' (code block) ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_{1,2}([^_]+)_{1,2}/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();
}

let currentUtterance: SpeechSynthesisUtterance | null = null;
let currentAudio: HTMLAudioElement | null = null;

/** Speaks the text; returns false when the browser has no speech synthesis. */
export function speak(text: string): boolean {
  if (!speakingSupported()) return false;
  stopSpeaking();
  const utterance = new SpeechSynthesisUtterance(stripMarkdownForSpeech(text));
  utterance.rate = 1.05;
  currentUtterance = utterance;
  window.speechSynthesis.speak(utterance);
  return true;
}

/** Speaks through the server worker when it answers, and falls back to the browser voice otherwise.
 *  Returns false only when both paths are unavailable. */
export async function speakSmart(text: string): Promise<boolean> {
  stopSpeaking();
  try {
    const response = await fetch('/api/tts', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text: stripMarkdownForSpeech(text) }),
    });
    if (response.ok) {
      const url = URL.createObjectURL(await response.blob());
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (currentAudio === audio) currentAudio = null;
      };
      currentAudio = audio;
      await audio.play();
      return true;
    }
  } catch {
    // Server voice unavailable; the browser voice below still works.
  }
  return speak(text);
}

export function stopSpeaking(): void {
  if (currentAudio) {
    currentAudio.pause();
    URL.revokeObjectURL(currentAudio.src);
    currentAudio = null;
  }
  if (!speakingSupported()) return;
  currentUtterance = null;
  window.speechSynthesis.cancel();
}

export function isSpeaking(): boolean {
  if (currentAudio !== null) return !currentAudio.paused;
  return speakingSupported() && (window.speechSynthesis.speaking || currentUtterance !== null);
}
